/**
 * Common const to handle http requests and responses
 * src/http.js
 */
import axios from 'axios'
import qs from 'qs'
import {LocalStorage} from 'wize-admin'
import router from '@/router'
import store from '@/store'

let isRefreshing = false
let failedQueue = []

function processQueue(error, token = null) {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error)
    else prom.resolve(token)
  })
  failedQueue = []
}

/**
 *
 * parse error response
 */
function parseError(messages) {
  // error
  if (messages) {
    if (messages instanceof Array) {
      return Promise.reject(new Error(messages[0]))
    }
    if (messages instanceof Object && messages.detail) {
      // JWT DRF returns an object error response
      return Promise.reject(messages.detail)
    } else {
      return Promise.reject(messages)
    }
  } else {
    return Promise.reject(new Error('Error'))
  }
}

/**
 * parse response
 */
function parseBody(response) {
  if (response.status === 200) {
    if (response.headers['content-disposition'])
      return response;
    else
      return response.data;
  } else {
    return parseError(response.data ? response.data.messages : response)
  }
}

/**
 * axios instance
 */
let instance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL + "/api/v1",
  paramsSerializer: function (params) {
    return qs.stringify(params, {indices: false})
  }
})

const requestUseConfig = (config) => {
  if (LocalStorage.getAccessToken() && !config.removeAuth) {
    config.headers['Authorization'] = 'Bearer ' + LocalStorage.getAccessToken();
  }
  return config
}

const responseUseError = async function (error) {
  let originalRequest = error.config

  // When responseType: 'blob', error.response.data is a Blob — convert to JSON for error inspection
  let responseData = error.response ? error.response.data : null
  if (responseData instanceof Blob && responseData.type === 'application/json') {
    try { responseData = JSON.parse(await responseData.text()) } catch (_) { /* non-parseable blob */ }
  }

  const user = JSON.parse(localStorage.getItem('user'));
  if (error.response && error.response.status === 401 && user && !originalRequest._retry) {

    // If a refresh is already in flight, queue this request until it resolves
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then(token => {
        originalRequest.headers['Authorization'] = 'Bearer ' + token
        return instance(originalRequest)
      }).catch(err => Promise.reject(err))
    }

    originalRequest._retry = true
    isRefreshing = true

    const refreshToken = LocalStorage.getRefreshToken()
    if (refreshToken) {
      try {
        // Use a plain axios call (no interceptors) so the expired Bearer token
        // is NOT attached to the refresh-token request
        const response = await axios.post(
          process.env.VUE_APP_API_BASE_URL + '/api/v1/auth/refresh-token',
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const tokens = response.data.payload
        store.dispatch('auth/setTokens', tokens)
        processQueue(null, tokens.accessToken)
        originalRequest.headers['Authorization'] = 'Bearer ' + tokens.accessToken
        return instance(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError)
        store.dispatch('auth/logout')
        router.push({ name: 'login' })
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    store.dispatch('auth/logout')
    router.push({ name: 'login' })
  }

  if (error.response && error.response.status === 422) {
    if (responseData && responseData.status === 'DOCUMENT_COMPLIANCE') {
      store.commit('compliance/setError', responseData.errors)
      return Promise.reject(error)
    }
  }

  return parseError(responseData)
}

// request header
instance.interceptors.request.use(requestUseConfig, error => {
  return Promise.reject(error)
})

instance.interceptors.response.use(function (response) {
  return parseBody(response);
}, responseUseError)

const http = instance

export {http}
